import { Initialize, FileType, SearchScope, } from "@warda/library-ui/interfaces";
import getMimeType from "@warda/library-ui/utils/getMimeType";
import getErrorMsg from "@warda/library-ui/utils/getErrorMsg";
import last from "@warda/library-ui/utils/last";
import { SearchMode, } from "../interfaces";
import apiUrls from "./apiUrlsMediaLibrary";
import { fetchCookieJwtWithRefreshToken } from "./fetchCookieJwt";
import { signUrl } from "./fetchesApiProducts";
import { AREA, AREA_TEXTS } from "../components/AreaMediaLibrary/constants";
import PERMS from "../constants/permissions";
import hasPerms from "../utils/hasPerms";
import getIdsFile from "../components/AreaMediaLibrary/AreaFiles/utils/getIdsFile";
import { normalizeElasticSrc } from "../utils/es/normalizeElasticSrc";
import { ERROR_GENERIC } from "../constants/keys";
import getNormalizedItems from "../utils/es/getNormalizedItems";
import { getArea } from "../components/AreaMediaLibrary/searchString";
import { isAreaTrash, isAreaPortal, } from "../components/AreaMediaLibrary/areasUtils";
export const createMyMediaLibrary = async () => {
    const { url, method } = apiUrls.createMyMediaLibrary;
    const res = await fetchCookieJwtWithRefreshToken({
        url: url(),
        method,
    });
    return res;
};
const composeFolderPathStart = (area) => {
    switch (area) {
        case AREA.TRASH_PORTAL:
            return [
                {
                    id: AREA.TRASH_PORTALS_LIST,
                    name: AREA_TEXTS[AREA.TRASH_PORTALS_LIST],
                },
            ];
        case AREA.PORTAL:
            return [
                {
                    id: AREA.PORTALS_LIST,
                    name: AREA_TEXTS[AREA.PORTALS_LIST],
                },
            ];
        case AREA.SHARED_WITH_ME:
            return [
                {
                    id: AREA.SHARED_WITH_ME,
                    name: AREA_TEXTS[AREA.SHARED_WITH_ME],
                },
            ];
        default:
            return [];
    }
};
const composeFolderData = (hrchOwners, items) => {
    var _a;
    try {
        const data = (_a = last(items)) === null || _a === void 0 ? void 0 : _a.data;
        return {
            ...data,
            owner: hrchOwners[data.hierarchyId] || data.owner,
        };
    }
    catch {
        return undefined;
    }
};
export const composeFolderPath = (area, items) => {
    const path = composeFolderPathStart(area);
    items.map((o) => {
        path.push({
            id: o.id,
            name: o.data.name,
        });
    });
    return path;
};
const mergeAggs = (oldAggs, newAggs) => {
    const aggregations = Array.from(oldAggs);
    newAggs.forEach((a) => {
        const agg = aggregations.find((agg) => agg.id === a.id);
        if (!!agg) {
            a.items.forEach((b) => {
                const aggItem = agg.items.find((aggItem) => aggItem.key === b.key);
                if (!!aggItem) {
                    aggItem.doc_count = aggItem.doc_count + b.doc_count;
                }
                else {
                    agg.items.push(b);
                }
            });
            agg.sum_other_doc_count = agg.sum_other_doc_count + a.sum_other_doc_count;
        }
        else {
            const agg = {
                id: a.id,
                items: a.items,
                sum_other_doc_count: a.sum_other_doc_count,
            };
            aggregations.push(agg);
        }
    });
    return aggregations;
};
export const SEARCH_FILES_RESULT_DEFAULT = {
    initSearch: Initialize.WAIT,
    folderData: undefined,
    folderPath: [],
    files: [],
    aggregations: [],
    filesTotal: {
        [FileType.FOLDER]: 0,
        [FileType.MEDIA]: 0,
        [FileType.PORTAL]: 0,
    },
    accessInherited: [],
};
export const SEARCH_FILES_RESULT_ERROR = {
    ...SEARCH_FILES_RESULT_DEFAULT,
    initSearch: Initialize.FAIL,
};
export const normalizeMediaLibrary = ({ area, res, }) => {
    var _a, _b, _c, _d;
    const error = ((_b = (_a = res.folder) === null || _a === void 0 ? void 0 : _a.error) === null || _b === void 0 ? void 0 : _b.root_cause[0].reason) ||
        ((_d = (_c = res.media) === null || _c === void 0 ? void 0 : _c.error) === null || _d === void 0 ? void 0 : _d.root_cause[0].reason);
    if (error) {
        return {
            ...SEARCH_FILES_RESULT_ERROR,
            error,
        };
    }
    const hrchOwners = res.hierarchiesOwners || {};
    const normM = normalizeElasticSrc(res.media);
    const normF = normalizeElasticSrc(res.folder);
    const normLocation = normalizeElasticSrc(res.folderLocation);
    const accessInherited = res.ownAndInheritedACL || [];
    const files = [];
    const filesTotal = {
        [FileType.FOLDER]: normF.itemsTotal,
        [FileType.MEDIA]: normM.itemsTotal,
        [FileType.PORTAL]: 0,
    };
    normF.items.map((o) => {
        const file = {
            id: o.id,
            type: FileType.FOLDER,
            data: {
                ...o.data,
                owner: hrchOwners[o.data.hierarchyId] || o.data.owner,
            },
            sort: o.sort,
        };
        files.push(file);
    });
    normM.items.map((o) => {
        const file = {
            id: o.id,
            type: FileType.MEDIA,
            data: {
                ...o.data,
                owner: hrchOwners[o.data.hierarchyId] || o.data.owner,
            },
            sort: o.sort,
        };
        files.push(file);
    });
    const folderData = composeFolderData(hrchOwners, normLocation.items);
    const folderPath = composeFolderPath(area, normLocation.items);
    if (!isAreaTrash() && !!(folderData === null || folderData === void 0 ? void 0 : folderData.deleted)) {
        return {
            initSearch: Initialize.FAIL,
            error: "deleted",
            folderData,
            folderPath,
            files,
            filesTotal,
            aggregations: [],
            accessInherited,
        };
    }
    const aggregations = [
        normM.aggregations,
        normF.aggregations,
    ].reduce(mergeAggs, []);
    return {
        initSearch: Initialize.SUCC,
        folderData,
        folderPath,
        files,
        filesTotal,
        aggregations,
        accessInherited,
    };
};
const searchFiles = async ({ area, hrch, prnt, searchMode, searchEsFolder, searchEsMedia, searchScope, }) => {
    const url = apiUrls.getFiles.url(area);
    const method = apiUrls.getFiles.method;
    if (!url)
        throw "Area not defined";
    const payload = {
        searchMode,
        folder: searchEsFolder,
        media: searchEsMedia,
        parentFolder: undefined,
        hierarchyId: undefined,
        searchSubfolders: searchMode === SearchMode.DEFAULT ||
            searchScope === SearchScope.ENTIRE_HIERARCHY
            ? undefined
            : searchScope === SearchScope.CURRENT_ONLY
                ? false
                : searchScope === SearchScope.CURRENT_AND_SUBFOLDERS
                    ? true
                    : undefined,
    };
    if (!!hrch) {
        if (searchMode === SearchMode.DEFAULT ||
            searchScope === SearchScope.CURRENT_AND_SUBFOLDERS ||
            searchScope === SearchScope.CURRENT_ONLY) {
            payload.parentFolder = {
                hierarchyId: hrch,
                folderId: prnt,
            };
        }
        else {
            payload.hierarchyId = hrch;
        }
    }
    if (area === AREA.SHARED_WITH_ME &&
        ((searchScope === SearchScope.CURRENT_AND_SUBFOLDERS && !!hrch) ||
            searchScope === SearchScope.CURRENT_ONLY)) {
        payload.searchMode = SearchMode.DEFAULT;
    }
    const res = await fetchCookieJwtWithRefreshToken({
        url,
        method,
        jsonBody: payload,
    });
    return normalizeMediaLibrary({ area, res });
};
export const getFiles = async (permissions, conf) => {
    const area = getArea();
    switch (area) {
        case AREA.PORTALS_LIST:
            return await getTouchpointList(permissions, conf, false);
        case AREA.TRASH_PORTALS_LIST:
            return await getTouchpointList(permissions, conf, true);
    }
    try {
        const res = await searchFiles(conf);
        return res;
    }
    catch (err) {
        const err404 = err.status === 404;
        const err400 = err.status === 400;
        if (err404 && !conf.prnt && !conf.hrch && conf.area === AREA.MYFILES) {
            // primo accesso alla sezione privata - creo la mia cartella
            try {
                const folderData = await createMyMediaLibrary();
                return {
                    ...SEARCH_FILES_RESULT_ERROR,
                    initSearch: Initialize.SUCC,
                    folderData,
                    folderPath: composeFolderPathStart(conf.area),
                };
            }
            catch (err2) {
                const errMsg2 = await getErrorMsg(err2);
                return { ...SEARCH_FILES_RESULT_ERROR, error: errMsg2 };
            }
        }
        let errMsg = ERROR_GENERIC;
        if (err404) {
            errMsg = isAreaPortal(conf.area)
                ? "Access denied"
                : "Directory not found";
            console.log(errMsg);
        }
        if (err400) {
            errMsg = "Invalid query parameters";
        }
        return { ...SEARCH_FILES_RESULT_ERROR, error: errMsg };
    }
};
export const getTouchpointList = async (permissions, conf, trash) => {
    var _a, _b;
    try {
        const isManager = hasPerms([PERMS.medialibrary_touchpoint_manager], permissions);
        const res = await fetchCookieJwtWithRefreshToken({
            url: apiUrls.getFiles.url(conf.area),
            method: isManager ? "POST" : "GET",
            jsonBody: conf.searchEsPortal,
        });
        if (res.error) {
            return {
                ...SEARCH_FILES_RESULT_ERROR,
                error: res.error,
            };
        }
        const normalizedItems = getNormalizedItems((_a = res === null || res === void 0 ? void 0 : res.response) !== null && _a !== void 0 ? _a : res);
        const hierarchiesHaveContent = (_b = res.hierarchiesHaveContent) !== null && _b !== void 0 ? _b : {};
        return {
            initSearch: Initialize.SUCC,
            folderData: undefined,
            folderPath: [],
            files: normalizedItems.items.map((item) => {
                var _a;
                const hierarchyId = item.data.hierarchyId;
                item.data.empty = trash
                    ? !((_a = hierarchiesHaveContent === null || hierarchiesHaveContent === void 0 ? void 0 : hierarchiesHaveContent[hierarchyId]) !== null && _a !== void 0 ? _a : true)
                    : undefined;
                return { ...item, type: FileType.PORTAL };
            }),
            filesTotal: {
                [FileType.FOLDER]: 0,
                [FileType.MEDIA]: 0,
                [FileType.PORTAL]: normalizedItems.itemsTotal,
            },
            aggregations: [],
            accessInherited: [],
        };
    }
    catch (err) {
        const errMsg = await getErrorMsg(err);
        return { ...SEARCH_FILES_RESULT_ERROR, error: errMsg };
    }
};
export const createFolders = async ({ hrch, prnt, tree, }) => {
    const { url, method } = apiUrls.createFolder;
    const res = await fetchCookieJwtWithRefreshToken({
        url: url(),
        method,
        jsonBody: {
            hierarchyId: hrch,
            containerFolderId: prnt,
            tree,
        },
    });
    return res;
};
export const createPortal = async (value) => {
    const { url, method } = apiUrls.createTouchpoint;
    const res = await fetchCookieJwtWithRefreshToken({
        url: url(),
        method,
        jsonBody: {
            touchpointName: value,
        },
    });
    return res;
};
export const deletePortal = async (hierarchyId) => {
    const { url, method } = apiUrls.deletePortal;
    const res = await fetchCookieJwtWithRefreshToken({
        url: url(hierarchyId),
        method,
    });
    return res;
};
export const deleteMedia = async (fileId, physical) => {
    const { url, method } = apiUrls.deleteMedia;
    const res = await fetchCookieJwtWithRefreshToken({
        url: url(fileId, physical),
        method,
    });
    return res;
};
export const deleteHrch = async (hrch) => {
    const { url, method } = apiUrls.deleteHrch;
    const res = await fetchCookieJwtWithRefreshToken({
        url: url(hrch),
        method,
    });
    return res;
};
export const deleteFiles = async ({ hrch, idsFolder, idsMedia, physical = false, }) => {
    if (idsMedia.length === 1 && idsFolder.length === 0) {
        const res = await deleteMedia(idsMedia[0], physical);
        return res;
    }
    else {
        const { url, method } = apiUrls.deleteFiles;
        const res = await fetchCookieJwtWithRefreshToken({
            url: url(),
            method,
            jsonBody: {
                hierarchyId: hrch,
                fileIds: idsMedia,
                folderIds: idsFolder,
                physicalDelete: physical,
            },
        });
        return res;
    }
};
export const mcrConfirm = async (fileId) => {
    const { url, method } = apiUrls.uploadConfirm;
    await fetchCookieJwtWithRefreshToken({
        url: url(fileId),
        method,
    });
};
export const getSignedUrl = () => async (ifile) => {
    const { hrch, prnt, file: { type, name }, } = ifile;
    const { url, method } = apiUrls.uploadNew;
    return await signUrl({
        url: url(),
        method,
        filename: name,
        mimeType: getMimeType(name, type),
        metadata: {
            location: {
                hierarchyId: hrch,
                folderId: prnt,
            },
        },
        targets: ["MEDIA_LIBRARY"],
        deferTenantSpecificRenditions: true,
    });
};
export const getFilesByDocumentId = async ({ area, hrch, mediaIds, folderIds, }) => {
    const { url, method } = apiUrls.getFiles;
    const res = await fetchCookieJwtWithRefreshToken({
        url: url(area),
        method,
        jsonBody: {
            hierarchyId: hrch,
            searchMode: SearchMode.HIERARCHY,
            folder: {
                query: {
                    bool: {
                        must: {
                            terms: {
                                _id: folderIds !== null && folderIds !== void 0 ? folderIds : [],
                            },
                        },
                    },
                },
            },
            media: {
                query: {
                    bool: {
                        must: {
                            terms: { _id: mediaIds !== null && mediaIds !== void 0 ? mediaIds : [] },
                        },
                    },
                },
            },
        },
    });
    const files = [];
    const hrchOwners = res.hierarchiesOwners || {};
    const normM = normalizeElasticSrc(res.media);
    const normF = normalizeElasticSrc(res.folder);
    normF.items.map((o) => {
        const file = {
            id: o.id,
            type: FileType.FOLDER,
            data: {
                ...o.data,
                owner: hrchOwners[o.data.hierarchyId] || o.data.owner,
            },
            sort: o.sort,
        };
        files.push(file);
    });
    normM.items.map((o) => {
        const file = {
            id: o.id,
            type: FileType.MEDIA,
            data: {
                ...o.data,
                owner: hrchOwners[o.data.hierarchyId] || o.data.owner,
            },
            sort: o.sort,
        };
        files.push(file);
    });
    return files;
};
export const getFilePath = async ({ area, hierarchyId, folderId, }) => {
    const url = apiUrls.getFiles.url(area);
    const method = apiUrls.getFiles.method;
    const res = await fetchCookieJwtWithRefreshToken({
        url,
        method,
        jsonBody: {
            searchMode: SearchMode.DEFAULT,
            parentFolder: {
                hierarchyId,
                folderId,
            },
            folder: { from: 0, size: 0 },
            media: { from: 0, size: 0 },
        },
    });
    const normLocation = normalizeElasticSrc(res.folderLocation);
    const folderPath = composeFolderPath(area, normLocation.items);
    const accessInherited = res.ownAndInheritedACL || [];
    return { folderPath, accessInherited };
};
export const restore = async (restoreFileData) => {
    const { url, method } = apiUrls.restore;
    await fetchCookieJwtWithRefreshToken({
        url: url(),
        method,
        jsonBody: restoreFileData,
    });
};
export const createPublicationMedia = async (fileId) => {
    const { url, method } = apiUrls.createPublication;
    await fetchCookieJwtWithRefreshToken({
        url: url(fileId),
        method,
    });
};
export const removePublicationMedia = async (fileId) => {
    const { url, method } = apiUrls.removePublication;
    await fetchCookieJwtWithRefreshToken({
        url: url(fileId),
        method,
    });
};
export const setSharepage = async ({ hrch, folderId, activate, validFrom, validUntil, }) => {
    const { url, method } = apiUrls.publicShare;
    await fetchCookieJwtWithRefreshToken({
        url: url(),
        method,
        jsonBody: {
            hierarchyId: hrch,
            folderId,
            activate,
            validFrom: validFrom ? new Date(validFrom).toISOString() : undefined,
            validUntil: validUntil ? new Date(validUntil).toISOString() : undefined,
        },
    });
};
export const setPwdSharepage = async ({ hierarchyId, folderId, password, }) => {
    const { url, method } = apiUrls.setPwdSharepage;
    await fetchCookieJwtWithRefreshToken({
        url: url(),
        method,
        jsonBody: {
            hierarchyId,
            folderId,
            password: password || null, // null elimina la password
        },
    });
};
export const getPwdSharepage = async ({ hierarchyId, folderId, }) => {
    const url = apiUrls.getPwdSharepage.url(hierarchyId, folderId);
    const method = apiUrls.getPwdSharepage.method;
    try {
        const data = await fetchCookieJwtWithRefreshToken({
            url,
            method,
        });
        return data === null || data === void 0 ? void 0 : data.value;
    }
    catch (err) {
        if ((err === null || err === void 0 ? void 0 : err.status) !== 400) {
            throw err;
        }
        return "";
    }
};
export const downloadMassiveML = async ({ correlationId, connectionId, groups, }) => {
    if (!connectionId) {
        return { success: false, message: "Missing 'websocketId'" };
    }
    if (!correlationId) {
        return { success: false, message: "Missing 'correlationId'" };
    }
    try {
        const { url, method } = apiUrls.downloadMassive;
        const message = await fetchCookieJwtWithRefreshToken({
            url: url(),
            method,
            jsonBody: {
                correlationId,
                connectionUUID: connectionId,
                renditionGroups: groups,
            },
        });
        return { success: true, message };
    }
    catch (err) {
        const message = await getErrorMsg(err);
        return { success: false, message };
    }
};
export const moveFiles = async ({ files, filesSlcId, destinationHrch, destinationPrnt, }) => {
    const { idsFolder, idsMedia } = getIdsFile(files, filesSlcId);
    const { url, method } = apiUrls.moveMedia;
    await fetchCookieJwtWithRefreshToken({
        url: url(),
        method,
        jsonBody: {
            hierarchyId: destinationHrch,
            folder: !idsFolder.length
                ? undefined
                : {
                    destinationFolder: destinationPrnt,
                    folderIds: idsFolder,
                },
            media: !idsMedia.length
                ? undefined
                : {
                    destinationFolder: destinationPrnt,
                    fileIds: idsMedia,
                },
        },
    });
};
export const updateMediaMetedataSet = async ({ idsMedia, setsToAdding, setsToRemove, }) => {
    if (idsMedia.length === 1) {
        const { url, method } = apiUrls.updateMediaMetedataSet;
        await fetchCookieJwtWithRefreshToken({
            url: url(idsMedia[0]),
            method,
            jsonBody: {
                setsToAdd: setsToAdding,
                setsToRemove: setsToRemove,
            },
        });
    }
    else {
        const { url, method } = apiUrls.updateMediaMetedataSetMassive;
        await fetchCookieJwtWithRefreshToken({
            url: url(),
            method,
            jsonBody: {
                setsToAdd: setsToAdding,
                setsToRemove: setsToRemove,
                filesId: idsMedia,
            },
        });
    }
};
export const updateMediaMetadata = async ({ idsMedia, data, }) => {
    const { url, method } = apiUrls.updateMediaMetedata;
    if (idsMedia.length > 1)
        throw "Not implemented";
    await fetchCookieJwtWithRefreshToken({
        url: url(idsMedia[0]),
        method,
        jsonBody: data,
    });
};
export const accessFiles = async ({ hrch, accesses }) => {
    const accessesFolder = [];
    const accessesMedia = [];
    const accessesTouch = [];
    accesses.forEach((f) => {
        if (f.type === FileType.FOLDER) {
            accessesFolder.push({
                folderId: Number(f.id),
                accesses: f.accesses,
            });
        }
        if (f.type === FileType.MEDIA) {
            accessesMedia.push({
                fileId: f.id,
                accesses: f.accesses,
            });
        }
        if (f.type === FileType.PORTAL) {
            accessesTouch.push({
                hierarchyId: f.id,
                accesses: f.accesses,
            });
        }
    });
    if (!!accessesFolder.length || !!accessesMedia.length) {
        const { url, method } = apiUrls.accessFiles;
        await fetchCookieJwtWithRefreshToken({
            url: url(),
            method,
            jsonBody: {
                filesAccess: accessesMedia,
                hierarchyAccess: !accessesFolder.length
                    ? undefined
                    : {
                        hierarchyId: hrch,
                        foldersAccess: accessesFolder,
                    },
            },
        });
    }
    if (accessesTouch.length === 1) {
        const { url, method } = apiUrls.accessTouch;
        await fetchCookieJwtWithRefreshToken({
            url: url(),
            method,
            jsonBody: {
                hierarchyId: accessesTouch[0].hierarchyId,
                accesses: accessesTouch[0].accesses,
            },
        });
    }
    else {
        console.log("not implemented multi touchpoint");
    }
};
export const renameFile = async (file, newName) => {
    if (file.type === FileType.MEDIA) {
        const { fileId } = file.data;
        const { url, method } = apiUrls.renameMedia;
        await fetchCookieJwtWithRefreshToken({
            url: url(),
            method,
            jsonBody: {
                newName,
                fileId,
            },
        });
    }
    if (file.type === FileType.FOLDER) {
        const { hierarchyId, folderId } = file.data;
        const { url, method } = apiUrls.renameFolder;
        await fetchCookieJwtWithRefreshToken({
            url: url(),
            method,
            jsonBody: {
                newName,
                folderId,
                hierarchyId,
            },
        });
    }
    if (file.type === FileType.PORTAL) {
        const { hierarchyId } = file.data;
        const { url, method } = apiUrls.renamePortal;
        await fetchCookieJwtWithRefreshToken({
            url: url(),
            method,
            jsonBody: {
                name: newName,
                hierarchyId,
            },
        });
    }
};
